import React, { useState, useEffect, useRef } from "react";
import { db } from "../../../firebase/config";
import { collection, query, where, getDocs, Timestamp } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import Chart from "chart.js/auto";

export default function Metricas() {
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const [periodo, setPeriodo] = useState('hoy');
  const [loading, setLoading] = useState(true);
  const [turnos, setTurnos] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [turnosReprogramadosCount, setTurnosReprogramadosCount] = useState(0);

  // Médicos configurados dinámicamente (medico1, medico2, medico3, medico4...)
  const medicosConfigurados = Object.entries(clinicaData?.medicos || {})
    .filter(([, nombre]) => nombre && nombre.trim())
    .sort(([a], [b]) => a.localeCompare(b)); // medico1, medico2, medico3...

  // Refs dinámicos: un array de refs para los canvas y otro para las instancias de Chart
  const chartMedicosRefs = useRef(medicosConfigurados.map(() => React.createRef()));
  const medicosInstances = useRef(medicosConfigurados.map(() => React.createRef()));

  // Si la cantidad de médicos cambia (ej: clinicaData carga después), regeneramos los refs
  if (chartMedicosRefs.current.length !== medicosConfigurados.length) {
    chartMedicosRefs.current = medicosConfigurados.map(() => React.createRef());
    medicosInstances.current = medicosConfigurados.map(() => React.createRef());
  }

  const chartDistribucion = useRef(null);
  const chartEstudios = useRef(null);
  const chartFinancieroARS = useRef(null);
  const chartFinancieroUSD = useRef(null);
  const chartTiposIngresos = useRef(null);
  const chartTiposEgresos = useRef(null);

  const distribucionInstance = useRef(null);
  const estudiosInstance = useRef(null);
  const financieroARSInstance = useRef(null);
  const financieroUSDInstance = useRef(null);
  const tiposIngresosInstance = useRef(null);
  const tiposEgresosInstance = useRef(null);

  const esAdmin = ['gestion_general', 'administracion'].includes(userRole);
  const esMedico = userRole === 'medico';
  const esRecepcion = userRole === 'recepcion';

  function getRango() {
    const ahora = new Date();
    const inicio = new Date();
    if (periodo === 'hoy') { inicio.setHours(0,0,0,0); }
    else if (periodo === 'semana') { inicio.setDate(ahora.getDate() - 7); }
    else if (periodo === 'mes') { inicio.setDate(1); inicio.setHours(0,0,0,0); }
    else if (periodo === 'anio') { inicio.setMonth(0); inicio.setDate(1); inicio.setHours(0,0,0,0); }
    return { inicio, fin: ahora };
  }

  useEffect(() => {
    if (!clinicaId) return;
    async function cargarDatos() {
      setLoading(true);
      const { inicio, fin } = getRango();

      const qTurnos = query(collection(db, 'turnos'), where('clinicaId', '==', clinicaId), where('fecha', '>=', Timestamp.fromDate(inicio)), where('fecha', '<=', Timestamp.fromDate(fin)));
      const snapTurnos = await getDocs(qTurnos);
      setTurnos(snapTurnos.docs.map(d => ({ id: d.id, ...d.data() })));

      const qReprog = query(collection(db, 'turnos'), where('clinicaId', '==', clinicaId), where('reprogramado', '==', true), where('creadoEn', '>=', Timestamp.fromDate(inicio)), where('creadoEn', '<=', Timestamp.fromDate(fin)));
      const snapReprog = await getDocs(qReprog);
      setTurnosReprogramadosCount(snapReprog.size);

      if (esAdmin) {
        const qMov = query(collection(db, 'caja'), where('clinicaId', '==', clinicaId), where('fecha', '>=', Timestamp.fromDate(inicio)), where('fecha', '<=', Timestamp.fromDate(fin)));
        const snapMov = await getDocs(qMov);
        setMovimientos(snapMov.docs.map(d => ({ id: d.id, ...d.data() })));
        const snapPac = await getDocs(query(collection(db, 'pacientes'), where('clinicaId', '==', clinicaId)));
        setPacientes(snapPac.docs.map(d => ({ id: d.id, ...d.data() })));
      }
      setLoading(false);
    }
    cargarDatos();
  }, [periodo, clinicaId]);

  const turnosAtendidos = turnos.filter(t => t.estado === 'atendido');
  const turnosCancelados = turnos.filter(t => t.estado === 'cancelado');
  const turnosAusentes = turnos.filter(t => t.estado === 'ausente');
  const turnosReprogramados = turnos.filter(t => t.reprogramado);
  const turnosProgramados = turnos.filter(t => !['cancelado','ausente'].includes(t.estado) && !turnosReprogramados.map(r => r.id).includes(t.id));
  const totalTurnos = turnosAtendidos.length + turnosCancelados.length + turnosAusentes.length;
  const estudios = turnosAtendidos.filter(t => t.tipo === 'estudio').length;
  const cirugias = turnosAtendidos.filter(t => t.tipo === 'cirugia').length;
  const consultasControles = turnosAtendidos.filter(t => ['consulta','control'].includes(t.tipo)).length;
  const tasaEstudios = consultasControles > 0 ? ((estudios / consultasControles) * 100).toFixed(1) + '%' : '—';
  const tasaCirugias = consultasControles > 0 ? ((cirugias / consultasControles) * 100).toFixed(1) + '%' : '—';

  // Datos por médico, calculados dinámicamente para cada médico configurado
  const datosPorMedico = medicosConfigurados.map(([key, nombre]) => ({
    key, nombre,
    atendidos: turnosAtendidos.filter(t => t.medico === key).length,
    total: turnos.filter(t => t.medico === key && ['atendido','cancelado','ausente'].includes(t.estado)).length,
  }));

  const ingresos = movimientos.filter(m => m.tipo === 'ingreso');
  const egresos = movimientos.filter(m => m.tipo === 'egreso');
  const totalIngresosARS = ingresos.filter(m => m.moneda === 'ARS').reduce((a, m) => a + m.monto, 0);
  const totalEgresosARS = egresos.filter(m => m.moneda === 'ARS').reduce((a, m) => a + m.monto, 0);
  const totalIngresosUSD = ingresos.filter(m => m.moneda === 'USD').reduce((a, m) => a + m.monto, 0);
  const totalEgresosUSD = egresos.filter(m => m.moneda === 'USD').reduce((a, m) => a + m.monto, 0);
  const { inicio } = getRango();
  const pacientesNuevos = pacientes.filter(p => { const c = p.creadoEn?.toDate ? p.creadoEn.toDate() : new Date(p.creadoEn); return c >= inicio; }).length;

  function agruparPorCategoria(lista) {
    const g = {};
    lista.forEach(m => { const k = m.categoria || 'Otro'; g[k] = (g[k] || 0) + m.monto; });
    return g;
  }

  const COLORES = [theme.accent, theme.success, theme.warning, theme.error, '#B478FF', '#FF8C00', '#00CED1', '#FF69B4'];
  const COLORES_BARRA = [theme.accent, theme.success, theme.warning, theme.error];

  useEffect(() => {
    if (loading) return;
    const destroyAll = () => {
      medicosInstances.current.forEach(r => { if (r.current) { r.current.destroy(); r.current = null; } });
      [distribucionInstance, estudiosInstance, financieroARSInstance, financieroUSDInstance, tiposIngresosInstance, tiposEgresosInstance]
        .forEach(r => { if (r.current) { r.current.destroy(); r.current = null; } });
    };
    destroyAll();

    const gridColor = theme.border;
    const tickColor = theme.secondary;

    // Un gráfico de barras por cada médico configurado
    if (esAdmin) {
      datosPorMedico.forEach((medico, i) => {
        const canvasEl = chartMedicosRefs.current[i]?.current;
        if (!canvasEl) return;
        const colorBarra = COLORES_BARRA[i % COLORES_BARRA.length];
        const instance = new Chart(canvasEl, {
          type: 'bar',
          data: { labels: [medico.nombre], datasets: [{ label: 'Total', data: [medico.total], backgroundColor: theme.secondary + '80', borderRadius: 4 }, { label: 'Atendidos', data: [medico.atendidos], backgroundColor: colorBarra, borderRadius: 4 }] },
          options: { responsive: true, plugins: { legend: { position: 'bottom', labels: { color: theme.secondary, font: { size: 11 } } }, title: { display: true, text: medico.nombre, color: theme.primary, font: { size: 12, weight: 'bold' } } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1, color: tickColor }, grid: { color: gridColor } }, x: { ticks: { color: tickColor }, grid: { color: gridColor } } } }
        });
        medicosInstances.current[i] = { current: instance };
      });
    }

    if ((esMedico || esAdmin) && chartDistribucion.current) {
      distribucionInstance.current = new Chart(chartDistribucion.current, {
        type: 'doughnut',
        data: { labels: ['Consulta', 'Control', 'Estudio', 'Cirugía'], datasets: [{ data: [turnosAtendidos.filter(t => t.tipo === 'consulta').length, turnosAtendidos.filter(t => t.tipo === 'control').length, estudios, cirugias], backgroundColor: COLORES.slice(0,4), borderWidth: 0 }] },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { color: theme.secondary, font: { size: 11 } } } } }
      });
    }

    if ((esMedico || esAdmin) && chartEstudios.current) {
      const turnosEstudio = turnosAtendidos.filter(t => t.tipo === 'estudio' && t.estudiosSeleccionados?.length > 0);
      const conteo = {};
      turnosEstudio.forEach(t => t.estudiosSeleccionados.forEach(e => { conteo[e] = (conteo[e] || 0) + 1; }));
      if (Object.keys(conteo).length > 0) {
        estudiosInstance.current = new Chart(chartEstudios.current, {
          type: 'doughnut',
          data: { labels: Object.keys(conteo), datasets: [{ data: Object.values(conteo), backgroundColor: COLORES, borderWidth: 0 }] },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { color: theme.secondary, font: { size: 10 } } } } }
        });
      }
    }

    if (esAdmin && chartFinancieroARS.current) {
      financieroARSInstance.current = new Chart(chartFinancieroARS.current, {
        type: 'bar',
        data: { labels: ['Ingresos ARS', 'Egresos ARS'], datasets: [{ data: [totalIngresosARS, totalEgresosARS], backgroundColor: [theme.success, theme.error], borderRadius: 4 }] },
        options: { responsive: true, plugins: { legend: { display: false }, title: { display: true, text: 'Pesos (ARS)', color: theme.primary, font: { size: 12 } } }, scales: { y: { beginAtZero: true, ticks: { callback: v => '$' + v.toLocaleString('es-AR'), color: tickColor, font: { size: 10 } }, grid: { color: gridColor } }, x: { ticks: { color: tickColor }, grid: { color: gridColor } } } }
      });
    }

    if (esAdmin && chartFinancieroUSD.current) {
      financieroUSDInstance.current = new Chart(chartFinancieroUSD.current, {
        type: 'bar',
        data: { labels: ['Ingresos USD', 'Egresos USD'], datasets: [{ data: [totalIngresosUSD, totalEgresosUSD], backgroundColor: [theme.accent, theme.warning], borderRadius: 4 }] },
        options: { responsive: true, plugins: { legend: { display: false }, title: { display: true, text: 'Dólares (USD)', color: theme.primary, font: { size: 12 } } }, scales: { y: { beginAtZero: true, ticks: { callback: v => 'USD ' + v.toFixed(2), color: tickColor, font: { size: 10 } }, grid: { color: gridColor } }, x: { ticks: { color: tickColor }, grid: { color: gridColor } } } }
      });
    }

    if (esAdmin && chartTiposIngresos.current) {
      const grupos = agruparPorCategoria(ingresos);
      if (Object.keys(grupos).length > 0) {
        tiposIngresosInstance.current = new Chart(chartTiposIngresos.current, {
          type: 'doughnut',
          data: { labels: Object.keys(grupos), datasets: [{ data: Object.values(grupos), backgroundColor: COLORES, borderWidth: 0 }] },
          options: { responsive: true, plugins: { legend: { position: 'bottom', labels: { color: theme.secondary, font: { size: 10 } } } } }
        });
      }
    }

    if (esAdmin && chartTiposEgresos.current) {
      const grupos = agruparPorCategoria(egresos);
      if (Object.keys(grupos).length > 0) {
        tiposEgresosInstance.current = new Chart(chartTiposEgresos.current, {
          type: 'doughnut',
          data: { labels: Object.keys(grupos), datasets: [{ data: Object.values(grupos), backgroundColor: COLORES, borderWidth: 0 }] },
          options: { responsive: true, plugins: { legend: { position: 'bottom', labels: { color: theme.secondary, font: { size: 10 } } } } }
        });
      }
    }

    return destroyAll;
  }, [loading, periodo, theme, medicosConfigurados.length]);

  const card = (label, valor, color, bg) => (
    <div key={label} style={{ background: bg, padding: '12px 14px', borderRadius: '10px' }}>
      <div style={{ fontSize: '11px', color, marginBottom: '4px', fontWeight: '500' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: '700', color }}>{valor}</div>
    </div>
  );

  const grafico = (titulo, canvasRef, height = 160) => (
    <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '14px' }}>
      <div style={{ fontSize: '12px', fontWeight: '600', color: theme.primary, marginBottom: '10px' }}>{titulo}</div>
      <div style={{ maxHeight: `${height}px`, position: 'relative' }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ color: theme.secondary }}>Cargando métricas...</div>
    </div>
  );

  const seccionTitulo = { fontSize: '11px', fontWeight: '700', color: theme.secondary, marginBottom: '12px', letterSpacing: '1.5px', textTransform: 'uppercase' };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Métricas" backLabel="Dashboard" backRuta="/dashboard" />
      <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', background: theme.bg }}>

        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {['hoy','semana','mes','anio'].map(p => (
            <button key={p} onClick={() => setPeriodo(p)} style={{
              padding: '6px 12px', fontSize: '12px', borderRadius: '8px', cursor: 'pointer',
              border: periodo === p ? 'none' : `0.5px solid ${theme.borderInput}`,
              background: periodo === p ? theme.accent : 'transparent',
              color: periodo === p ? '#FFFFFF' : theme.secondary,
              fontFamily: "'Montserrat', sans-serif", fontWeight: periodo === p ? '600' : '400'
            }}>
              {p === 'hoy' ? 'Hoy' : p === 'semana' ? '7 días' : p === 'mes' ? 'Este mes' : 'Este año'}
            </button>
          ))}
        </div>

        <div>
          <div style={seccionTitulo}>Turnos operativos</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px', marginBottom: '16px' }}>
            {card('Total turnos', totalTurnos, theme.accent, theme.infoBg)}
            {card('Atendidos', turnosAtendidos.length, theme.success, theme.successBg)}
            {card('Cancelados', turnosCancelados.length, theme.error, theme.errorBg)}
            {card('Ausentes', turnosAusentes.length, theme.warning, theme.warningBg)}
            {card('Reprogramados', turnosReprogramadosCount || turnosReprogramados.length, theme.secondary, theme.bgCard)}
            {card('Tasa atención', totalTurnos > 0 ? ((turnosAtendidos.length / totalTurnos) * 100).toFixed(0) + '%' : '—', '#FFFFFF', theme.accent)}
          </div>
          {esAdmin && medicosConfigurados.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: medicosConfigurados.length === 1 ? '1fr' : medicosConfigurados.length === 2 ? '1fr 1fr' : 'repeat(auto-fit, minmax(380px, 1fr))', gap: '12px' }}>
              {medicosConfigurados.map((medico, i) => (
                <div key={medico[0]} style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '14px' }}>
                  <div style={{ maxHeight: '300px', position: 'relative' }}>
                    <canvas ref={chartMedicosRefs.current[i]} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {(esMedico || esAdmin) && (
          <div>
            <div style={seccionTitulo}>Análisis clínico</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px', marginBottom: '16px' }}>
              {card('Consultas', turnosAtendidos.filter(t => t.tipo === 'consulta').length, theme.accent, theme.infoBg)}
              {card('Controles', turnosAtendidos.filter(t => t.tipo === 'control').length, theme.success, theme.successBg)}
              {card('Estudios', estudios, theme.warning, theme.warningBg)}
              {card('Cirugías', cirugias, theme.error, theme.errorBg)}
              {card('Tasa estudios', tasaEstudios, theme.secondary, theme.bgCard)}
              {card('Tasa cirugías', tasaCirugias, theme.secondary, theme.bgCard)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {grafico('Distribución de tipos de turno', chartDistribucion, 300)}
              {grafico('Distribución de estudios diagnósticos', chartEstudios, 300)}
            </div>
          </div>
        )}

        {(esMedico || esRecepcion || esAdmin) && (
          <div>
            <div style={seccionTitulo}>Tiempos operativos</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
              {[
                { label: 'Tiempo promedio de espera', valor: '—', sub: 'Desde llegada hasta consultorio' },
                { label: 'Tiempo promedio de atención', valor: '—', sub: 'Desde llegada hasta atendido' },
              ].map(item => (
                <div key={item.label} style={{ background: theme.bgCard, padding: '14px 16px', borderRadius: '10px', border: `0.5px solid ${theme.border}` }}>
                  <div style={{ fontSize: '11px', color: theme.secondary, marginBottom: '4px', fontWeight: '500' }}>{item.label}</div>
                  <div style={{ fontSize: '26px', fontWeight: '700', color: theme.primary, marginBottom: '2px' }}>{item.valor}</div>
                  <div style={{ fontSize: '11px', color: theme.secondary }}>{item.sub}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(esRecepcion || esAdmin) && (
          <div>
            <div style={seccionTitulo}>Operaciones de recepción</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
              {card('Ingresados', turnos.filter(t => t.timestampEspera).length, theme.accent, theme.infoBg)}
              {card('Atendidos', turnosAtendidos.length, theme.success, theme.successBg)}
              {card('Ausentes', turnosAusentes.length, theme.warning, theme.warningBg)}
              {card('Reprogramados', turnosReprogramadosCount || turnosReprogramados.length, theme.secondary, theme.bgCard)}
              {card('Tasa reprogramación', turnosProgramados.length > 0 ? ((turnosReprogramados.length / turnosProgramados.length) * 100).toFixed(0) + '%' : '—', theme.secondary, theme.bgCard)}
            </div>
          </div>
        )}

        {esAdmin && (
          <div>
            <div style={seccionTitulo}>Finanzas</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px', marginBottom: '16px' }}>
              {card('Ingresos ARS', '$' + totalIngresosARS.toLocaleString('es-AR', {minimumFractionDigits:0}), theme.success, theme.successBg)}
              {card('Egresos ARS', '$' + totalEgresosARS.toLocaleString('es-AR', {minimumFractionDigits:0}), theme.error, theme.errorBg)}
              {card('Resultado ARS', '$' + (totalIngresosARS - totalEgresosARS).toLocaleString('es-AR', {minimumFractionDigits:0}), (totalIngresosARS - totalEgresosARS) >= 0 ? theme.success : theme.error, (totalIngresosARS - totalEgresosARS) >= 0 ? theme.successBg : theme.errorBg)}
              {card('Ingresos USD', 'USD ' + totalIngresosUSD.toFixed(2), theme.accent, theme.infoBg)}
              {card('Egresos USD', 'USD ' + totalEgresosUSD.toFixed(2), theme.warning, theme.warningBg)}
              {card('Resultado USD', 'USD ' + (totalIngresosUSD - totalEgresosUSD).toFixed(2), (totalIngresosUSD - totalEgresosUSD) >= 0 ? theme.success : theme.error, (totalIngresosUSD - totalEgresosUSD) >= 0 ? theme.successBg : theme.errorBg)}
              {card('Pacientes nuevos', pacientesNuevos, theme.accent, theme.infoBg)}
              {card('Total pacientes', pacientes.length, theme.secondary, theme.bgCard)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
              {grafico('Ingresos vs Egresos ARS', chartFinancieroARS)}
              {grafico('Ingresos vs Egresos USD', chartFinancieroUSD)}
              {grafico('Tipos de ingresos', chartTiposIngresos)}
              {grafico('Tipos de egresos', chartTiposEgresos)}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}